// Permission catalogue for employee logins.
//
// KEEP IN SYNC with the API twin: nvision_dental_api/functions/permissions.js
// The two repos are independent so the key list is unavoidably duplicated. That copy owns
// validation and route-mount mapping; this one owns the human labels, the grouping for the
// checkbox UI, and the URL -> key mapping used to guard pages.
//
// Hiding a sidebar item is NOT security - the employee's token is a normal JWT and the API is
// what actually enforces this. These keys exist so the UI matches what the server will allow.

export const PERMISSION_GROUPS = [
    {
        group: 'Dashboard & Messages',
        items: [
            { key: 'dashboard', label: 'Dashboard' },
            { key: 'messages', label: 'Messages' }
        ]
    },
    {
        group: 'Orders',
        items: [
            { key: 'orders', label: 'Orders (view, create, edit)' },
            { key: 'orders.status', label: 'Order statuses' },
            { key: 'orders.departments', label: 'Order departments' }
        ]
    },
    {
        group: 'Money',
        items: [
            { key: 'billing', label: 'Billing' },
            { key: 'billing.payments', label: 'Payments' },
            { key: 'expenses', label: 'Expenses' },
            { key: 'accounts', label: 'Accounts, vouchers & transactions' },
            { key: 'reports', label: 'Reports' }
        ]
    },
    {
        group: 'Entries (master data)',
        items: [
            { key: 'entries.products', label: 'Products & services' },
            { key: 'entries.materials', label: 'Materials' },
            { key: 'entries.clinics', label: 'Clinics' },
            { key: 'entries.doctors', label: 'Doctors' },
            { key: 'entries.employees', label: 'Employees' }
        ]
    },
    {
        group: 'Printing',
        items: [
            { key: 'print.jobcard', label: 'Job cards & labels' },
            { key: 'print.billing', label: 'Billing statements' },
            { key: 'print.sales', label: 'Sales reports' },
            { key: 'print.expense', label: 'Expense reports' },
            { key: 'print.profitandloss', label: 'Profit & loss' }
        ]
    },
    {
        group: 'Other',
        items: [
            { key: 'shipping', label: 'Shipping' }
        ]
    }
];

export const ALL_PERMISSIONS = PERMISSION_GROUPS.reduce(
    (all, g) => all.concat(g.items.map((i) => i.key)), []
);

// URL -> required permission key.
//
// ORDER MATTERS: matched top to bottom on prefix, so the more specific path must come first.
// '/labadmin/orders/status' has to be tested before '/labadmin/orders' or it would resolve to
// the broader `orders` key and a technician with `orders` would reach the status editor.
const ROUTE_RULES = [
    { prefix: '/labadmin/dashboard', key: 'dashboard' },
    { prefix: '/labadmin/messages', key: 'messages' },
    { prefix: '/labadmin/orders/status', key: 'orders.status' },
    { prefix: '/labadmin/orders/departments', key: 'orders.departments' },
    { prefix: '/labadmin/orders', key: 'orders' },
    { prefix: '/labadmin/billing/payments', key: 'billing.payments' },
    { prefix: '/labadmin/billing', key: 'billing' },
    { prefix: '/labadmin/expenses', key: 'expenses' },
    { prefix: '/labadmin/accounts', key: 'accounts' },
    { prefix: '/labadmin/reports', key: 'reports' },
    { prefix: '/labadmin/shipping', key: 'shipping' },
    { prefix: '/labadmin/entries/products', key: 'entries.products' },
    { prefix: '/labadmin/entries/materials', key: 'entries.materials' },
    { prefix: '/labadmin/entries/clinics', key: 'entries.clinics' },
    { prefix: '/labadmin/entries/doctors', key: 'entries.doctors' },
    { prefix: '/labadmin/entries/employees', key: 'entries.employees' },
    // The 13 print routes collapse onto 5 keys. jobcard/jobcardnp/label are shop-floor
    // documents a technician needs; the financial ones are separate on purpose.
    { prefix: '/labadminreport/jobcard', key: 'print.jobcard' },
    { prefix: '/labadminreport/jobcardnp', key: 'print.jobcard' },
    { prefix: '/labadminreport/label', key: 'print.jobcard' },
    { prefix: '/labadminreport/billingdetailed', key: 'print.billing' },
    { prefix: '/labadminreport/billing', key: 'print.billing' },
    { prefix: '/labadminreport/salesbydate', key: 'print.sales' },
    { prefix: '/labadminreport/salesbyrange', key: 'print.sales' },
    { prefix: '/labadminreport/dailysalesreport', key: 'print.sales' },
    { prefix: '/labadminreport/rangesalesreport', key: 'print.sales' },
    { prefix: '/labadminreport/expensebydate', key: 'print.expense' },
    { prefix: '/labadminreport/expensebyrange', key: 'print.expense' },
    { prefix: '/labadminreport/profitandlossdate', key: 'print.profitandloss' },
    { prefix: '/labadminreport/profitandlossrange', key: 'print.profitandloss' }
];

// The permission a path needs, or null when it needs none (e.g. the /labadmin index redirect).
export const permissionForPath = (pathname) => {
    const path = String(pathname || '').toLowerCase();
    const rule = ROUTE_RULES.find((r) => path.indexOf(r.prefix) === 0);
    return rule ? rule.key : null;
};

// The single place that answers "may this user see X".
// Only employees are restricted; labadmin keeps full access to their own lab, and admin/doctor
// never reach these pages at all.
export const can = (user, key) => {
    if (!user) return false;
    if (user.role !== 'employee') return true;
    if (!key) return true;
    return Array.isArray(user.permissions) && user.permissions.indexOf(key) !== -1;
};

// First page an employee is allowed to see, for the post-login redirect and for bouncing them
// off a page they cannot open. Returns null when nothing is permitted at all.
export const firstAllowedPath = (user) => {
    if (!user) return null;
    if (user.role !== 'employee') return '/labadmin/dashboard';
    const rule = ROUTE_RULES.find((r) => r.prefix.indexOf('/labadmin/') === 0 && can(user, r.key));
    return rule ? rule.prefix : null;
};
